/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON decision 58: a person never sees ":7ion.org" nor a whole iON ID.
 * Someone of our own server is shown as "@nickname" (the iON profile field)
 * when it is known, otherwise as the short iON ID "@0x2bdb…3966". Someone of
 * another server is shown in full, so that "@alex:evil.org" cannot pass for
 * our "@alex". Only what is shown changes: events and API calls keep the
 * full ID.
 */

import { BehaviorSubject } from "rxjs";
import { logger as rootLogger } from "matrix-js-sdk/lib/logger";

let ownServerName: string | null = null;
let fetchNickname: ((userId: string) => Promise<string | null>) | null = null;
const requested = new Set<string>();

/** Nicknames loaded so far, by user ID. */
export const nicknames$ = new BehaviorSubject<ReadonlyMap<string, string>>(
  new Map(),
);

/**
 * Remembers the signed-in account: IDs of its server are shown short, and
 * nicknames are loaded with `fetch` if given (a widget has no way to).
 */
export function setOwnUserId(
  userId: string | null | undefined,
  fetch: ((userId: string) => Promise<string | null>) | null = null,
): void {
  ownServerName = (userId && split(userId)?.server) || null;
  fetchNickname = fetch;
  requested.clear();
  nicknames$.next(new Map());
}

/**
 * The Matrix ID as it is shown to a person: "@nickname" or "@0x2bdb…3966"
 * for our own server, in full otherwise. Anything that is not a Matrix ID
 * is returned as it is.
 */
export function displayUserId(
  userId: string,
  ownServer: string | null = ownServerName,
  nicknames: ReadonlyMap<string, string> = nicknames$.value,
): string {
  const parts = split(userId);
  if (!parts || !ownServer || parts.server !== ownServer) return userId;
  const nickname = nicknames.get(userId);
  if (nickname) return `@${nickname}`;
  return `@${shortLocalpart(parts.localpart)}`;
}

/**
 * Whether a display name says nothing beyond the ID: Synapse sets the
 * localpart ("0x…") as the name of a new account.
 */
export function isPlaceholderName(name: string, userId: string): boolean {
  const parts = split(userId);
  return (
    name === userId ||
    (parts !== null && name === parts.localpart) ||
    WALLET.test(name)
  );
}

/** Loads the nickname of someone of our own server, once. */
export function requestNickname(userId: string): void {
  const parts = split(userId);
  if (!fetchNickname || !parts || parts.server !== ownServerName) return;
  if (requested.has(userId)) return;
  requested.add(userId);
  fetchNickname(userId).then(
    (nickname) => {
      if (!nickname) return;
      const next = new Map(nicknames$.value);
      next.set(userId, nickname);
      nicknames$.next(next);
    },
    (e) =>
      rootLogger.getChild("[ion shortId]").info(`No nickname for ${userId}`, e),
  );
}

const WALLET = /^0x[0-9a-f]{40}$/i;

function shortLocalpart(localpart: string): string {
  return WALLET.test(localpart)
    ? `${localpart.slice(0, 6)}…${localpart.slice(-4)}`
    : localpart;
}

function split(userId: string): { localpart: string; server: string } | null {
  const match = /^@([^:]+):(.+)$/.exec(userId);
  return match ? { localpart: match[1], server: match[2] } : null;
}
